import { useEffect, useRef } from 'react'

type FbqArgs = Record<string, any>

const CATEGORY = 'Médico en Proceso'

/**
 * Fire a standard Meta Pixel event (PageView, ViewContent, InitiateCheckout, Lead, etc.)
 * Safe to call before fbq is loaded — events queue up automatically.
 */
export function track(event: string, data?: FbqArgs) {
  if (typeof window === 'undefined') return
  const fbq = (window as any).fbq
  if (typeof fbq === 'function') {
    fbq('track', event, data)
  }
}

/**
 * Fire a custom Meta Pixel event (not in Meta's standard event list).
 */
export function trackCustom(event: string, data?: FbqArgs) {
  if (typeof window === 'undefined') return
  const fbq = (window as any).fbq
  if (typeof fbq === 'function') {
    fbq('trackCustom', event, data)
  }
}

/**
 * Returns a ref that, when attached to an element, fires a Meta Pixel
 * `ViewContent` event the first time at least 30% of the element is visible.
 */
export function useSectionView(sectionName: string, threshold: number = 0.3) {
  const ref = useRef<HTMLElement | null>(null)
  const firedRef = useRef(false)

  useEffect(() => {
    const node = ref.current
    if (!node || firedRef.current) return
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true
            track('ViewContent', {
              content_name: sectionName,
              content_category: CATEGORY,
            })
            observer.disconnect()
            break
          }
        }
      },
      { threshold },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [sectionName, threshold])

  return ref as React.MutableRefObject<any>
}

/**
 * Sets up GLOBAL Meta Pixel tracking across the entire page:
 * - Every click (button, link, any interactive element) → 'ClickInteraction'
 * - Scroll depth milestones (25/50/75/100%) → 'ScrollDepth'
 * - Time on page milestones (15s, 30s, 60s, 180s) → 'TimeOnPage'
 * - Section visibility (every <section>) → 'ViewSection'
 * - Page leave / tab hide → 'PageLeave' with engagement metadata
 *
 * Idempotent: safe to call multiple times — only attaches once.
 * Returns a cleanup function (useful for tests / hot-reload).
 */
export function initGlobalTracking() {
  if (typeof window === 'undefined') return () => {}
  const w = window as any
  if (w.__mpTrackingInit) return w.__mpTrackingCleanup || (() => {})
  w.__mpTrackingInit = true

  const startTime = Date.now()
  let maxScroll = 0
  const scrollMilestones = [25, 50, 75, 100]
  const firedScroll = new Set<number>()
  const timeMilestones = [15, 30, 60, 180]
  const firedTime = new Set<number>()
  let clickCount = 0

  // ── CLICK TRACKING (event delegation, catches everything) ──
  const onClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null
    if (!target) return
    const interactive = target.closest(
      'a, button, [role="button"], input[type="button"], input[type="submit"]',
    ) as HTMLElement | null
    if (!interactive) return

    clickCount++
    const tag = interactive.tagName.toLowerCase()
    const text = (interactive.textContent || '').trim().slice(0, 80)
    const href = (interactive as HTMLAnchorElement).href || ''
    const ariaLabel = interactive.getAttribute('aria-label') || ''
    const isCheckout = /cart\/|checkout/i.test(href)

    trackCustom('ClickInteraction', {
      element: tag,
      label: text || ariaLabel || 'unlabeled',
      href: href || undefined,
      is_checkout_link: isCheckout || undefined,
      click_index: clickCount,
      content_category: CATEGORY,
    })

    // Bonus: if it's a checkout link, also fire standard Lead event
    if (isCheckout) {
      track('Lead', {
        content_name: text || 'Checkout Link',
        content_category: CATEGORY,
      })
    }
  }

  // ── SCROLL DEPTH ──
  const onScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    if (docHeight <= 0) return
    const pct = Math.min(100, Math.round((scrollTop / docHeight) * 100))
    if (pct > maxScroll) maxScroll = pct

    for (const milestone of scrollMilestones) {
      if (pct >= milestone && !firedScroll.has(milestone)) {
        firedScroll.add(milestone)
        trackCustom('ScrollDepth', {
          depth_percent: milestone,
          content_category: CATEGORY,
        })
      }
    }
  }

  // ── TIME ON PAGE ──
  const timeInterval = window.setInterval(() => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000)
    for (const milestone of timeMilestones) {
      if (elapsed >= milestone && !firedTime.has(milestone)) {
        firedTime.add(milestone)
        trackCustom('TimeOnPage', {
          seconds: milestone,
          content_category: CATEGORY,
        })
      }
    }
    if (firedTime.size === timeMilestones.length) {
      window.clearInterval(timeInterval)
    }
  }, 5000)

  // ── SECTION VISIBILITY (auto-attach to every <section>) ──
  const sectionObserver =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                const el = entry.target as HTMLElement
                if (el.dataset.mpSeen) continue
                el.dataset.mpSeen = '1'
                const name =
                  el.id ||
                  el.getAttribute('data-section') ||
                  el
                    .querySelector('h1, h2, h3')
                    ?.textContent?.trim()
                    .slice(0, 60) ||
                  'unnamed-section'
                trackCustom('ViewSection', {
                  section_name: name,
                  content_category: CATEGORY,
                })
                sectionObserver?.unobserve(el)
              }
            }
          },
          { threshold: 0.4 },
        )
      : null

  const observeSections = () => {
    if (!sectionObserver) return
    document.querySelectorAll('section').forEach((s) => {
      if (!(s as HTMLElement).dataset.mpSeen) sectionObserver.observe(s)
    })
  }

  // Run once now, and again on DOM mutations (covers lazy-rendered sections)
  observeSections()
  const mutationObserver =
    typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => observeSections())
      : null
  mutationObserver?.observe(document.body, { childList: true, subtree: true })

  // ── PAGE LEAVE / TAB HIDE ──
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      const elapsed = Math.floor((Date.now() - startTime) / 1000)
      trackCustom('PageLeave', {
        time_on_page_seconds: elapsed,
        max_scroll_percent: maxScroll,
        click_count: clickCount,
        content_category: CATEGORY,
      })
    }
  }

  document.addEventListener('click', onClick, { capture: true, passive: true })
  window.addEventListener('scroll', onScroll, { passive: true })
  document.addEventListener('visibilitychange', onVisibility)

  // Initial scroll measurement (in case user lands mid-page)
  onScroll()

  const cleanup = () => {
    document.removeEventListener('click', onClick, { capture: true } as any)
    window.removeEventListener('scroll', onScroll)
    document.removeEventListener('visibilitychange', onVisibility)
    window.clearInterval(timeInterval)
    sectionObserver?.disconnect()
    mutationObserver?.disconnect()
    w.__mpTrackingInit = false
  }
  w.__mpTrackingCleanup = cleanup
  return cleanup
}
